import { Box, Spinner } from '@gluestack-ui/themed';
import { useEffect, useState, useMemo, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useGetProgramsQuery } from 'services/cms';
import { RootState } from 'store/index';
import { ProgramType } from 'utils/enum';
import { calcSizeForDevice } from 'utils/index';
import { useDebounce } from 'utils/useDebounce';
import { useAnalytics } from 'services/analytics/useAnalytics';

import Section from 'components/Section';
import { Platform } from 'utils/platform';

type SearchVerticalListProps = {
  searchInputValue?: string;
  categoryValue?: ProgramType;
  containerWidth?: number | undefined;
  topContainerHeight?: number | undefined;
};
const SearchVerticalList: React.FC<SearchVerticalListProps> = ({
  categoryValue,
  searchInputValue,
  ...props
}) => {
  const [page, setPage] = useState(1);
  const [items, setItems] = useState<any[]>([]);
  const [hasMorePages, setHasMorePages] = useState(true);
  const analytics = useAnalytics();

  const delayedValue = useDebounce(searchInputValue);

  const profile = useSelector((state: RootState) => state.profile);

  const { data, isLoading, isFetching, isSuccess } = useGetProgramsQuery({
    q: delayedValue,
    type: categoryValue,
    page,
  });

  useEffect(() => {
    setItems([]);
    setPage(1);
     setHasMorePages(true);

    if (delayedValue) analytics.triggerSearchEvent(delayedValue);
  }, [delayedValue, categoryValue, profile.ageRating]);


  useEffect(() => {
    if (isFetching || !isSuccess) return;

    if (data?.items) {
      if (page === 1) {
         setItems([...data.items]);
      } else {
         setItems((oldItems) => {
            const newItems = data.items.filter(
                (newItem: any) => !oldItems.find((oldItem) => oldItem._id === newItem._id)
            );
            return [...oldItems, ...newItems];
        });
      }

      const lastFetchItemCount = data.items.length;
      const limitPerPage = data.meta?.limit || (Platform.isTV ? 8 : 10);
      setHasMorePages(lastFetchItemCount >= limitPerPage);

    } else {
       if(page === 1) {
         setItems([]);
       }
       setHasMorePages(false);
    }
  }, [data, isFetching, isSuccess]);


  const formattedData: CmsCard[] = useMemo(() => {
    return items.map<CmsCard>((item) => ({
      type: item.type,
      title: item.title,
      image: item.imageVertical || item.image,
      link: `/${item.url}`,
      height: item.imageVertical?.height || item.image?.height,
       _id: item._id,
       id: item.id,
       search_term: delayedValue,
       search_category: categoryValue,
    })) || [];
   }, [items, delayedValue, categoryValue]);


  const HandleOnEndReached = useCallback(() => {
    if (!isFetching && hasMorePages) {
        setPage((p) => p + 1);
    }
  }, [isFetching, hasMorePages]);


  const showInitialLoading = isLoading && page === 1;
  const showPaginationLoading = isFetching && page > 1;

   if (showInitialLoading) {
    return (
       <Box flex={1} h={200} justifyContent="center" alignItems="center">
         <Spinner size="large" />
       </Box>
     );
  }

  return (
    <Box flexDirection="column" flex={1}>
       <Section
        onEndReached={HandleOnEndReached}
        vertical
        items={formattedData}
        _name="searchResultsSection"
        showTitle={false}
        variant="section.medium"
        title={searchInputValue || 'Resultados da Busca'}
        {...props}
      />
      {showPaginationLoading && (
         <Box p="$4" w="$full" h={50} alignItems="center" justifyContent="center" >
          <Spinner size="small" />
        </Box>
      )}
    </Box>
  );
};

export default SearchVerticalList;